import { Controller } from "@hotwired/stimulus"
import TomSelect from "tom-select"

export default class extends Controller {
  static values = {
    type: String,
    placeholder: String,
    autosuggestUrl: String,
    createRecordUrl: String,
    showCategory: { type: Boolean, default: false },
    showPending: { type: Boolean, default: true },
    submitOnChange: { type: Boolean, default: false },
    hideSearch: { type: Boolean, default: false },
    maxItems: Number
  }

  connect() {
    this.cleanupOrphanWrapper()

    if (this.element.tomselect) {
      this.tomSelect = this.element.tomselect
      return
    }

    const options = this.hasTypeValue ? this.remoteOptions() : this.localOptions()
    this.tomSelect = new TomSelect(this.element, options)
    this.hydrateOptionMetadataFromDom()
  }

  disconnect() {
    this.tomSelect?.destroy()
  }

  hydrateOptionMetadataFromDom() {
    if (!this.tomSelect) return

    Array.from(this.element.options).forEach((domOption) => {
      const value = String(domOption.value || "")
      const option = this.tomSelect.options[value]
      if (!option) return

      if (!option.icon_url && domOption.dataset.iconUrl) {
        option.icon_url = domOption.dataset.iconUrl
      }
    })
  }

  localOptions() {
    const isMulti = this.element.multiple
    const maxItems = this.hasMaxItemsValue ? this.maxItemsValue : (isMulti ? null : 1)
    const plugins = isMulti ? ["remove_button"] : []

    if (!isMulti && !this.hideSearchValue) {
      plugins.push("dropdown_input")
    }

    return {
      create: false,
      allowEmptyOption: true,
      plugins,
      maxItems,
      openOnFocus: true,
      controlInput: this.hideSearchValue ? null : undefined,
      searchField: this.hideSearchValue ? [] : ["text", "label", "value"],
      sortField: [{ field: "text", direction: "asc" }],
      render: this.renderOptions(),
      onChange: () => this.submitOnChange()
    }
  }

  remoteOptions() {
    const base = {
      plugins: [],
      valueField: "value",
      labelField: "label",
      searchField: ["label", "category"],
      sortField: [{ field: "label", direction: "asc" }],
      placeholder: this.placeholderValue || "Search...",
      maxItems: 1,
      preload: "focus",
      load: this.loadData.bind(this),
      render: this.renderOptions()
    }

    if (this.typeValue === "single-create-remote") {
      base.create = (input, callback) => this.createRecord(input, callback)
    } else {
      base.create = false
    }

    if (this.typeValue === "single") {
      delete base.preload
      delete base.load
      delete base.render
      base.searchField = ["text"]
      base.controlInput = this.hideSearchValue ? null : undefined
    }

    return base
  }

  cleanupOrphanWrapper() {
    if (this.element.tomselect) return

    const next = this.element.nextElementSibling
    if (next && next.classList.contains("ts-wrapper")) {
      next.remove()
    }
  }

  renderOptions() {
    const controller = this
    return {
      option(data, escape) {
        const label = escape(data.label || data.text || data.value || "")
        const icon = data.icon_url ? `<img src="${escape(data.icon_url)}" alt="" class="h-4 w-4 rounded-sm object-contain" loading="lazy">` : ""
        const category = data.category ? `<span class="text-xs text-slate-500">${escape(data.category)}</span>` : ""
        const pending = controller.showPendingValue && data.pending ? `<span class="ml-auto rounded bg-amber-100 px-1.5 py-0.5 text-[10px] text-amber-800">pending</span>` : ""
        return `<div class="flex items-center gap-2">${icon}<span class="min-w-0 flex-1">${label}</span>${category}${pending}</div>`
      },
      item(data, escape) {
        const label = escape(data.label || data.text || data.value || "")
        const icon = data.icon_url ? `<img src="${escape(data.icon_url)}" alt="" class="h-4 w-4 rounded-sm object-contain" loading="lazy">` : ""
        const pending = controller.showPendingValue && data.pending ? `<span class="ml-1 text-amber-500">⏳</span>` : ""
        return `<div class="flex items-center gap-1">${icon}${label}${pending}</div>`
      },
      option_create(data, escape) {
        return `<div class="create text-emerald-600">Add "<strong>${escape(data.input)}</strong>"...</div>`
      }
    }
  }

  loadData(query, callback) {
    if (!this.hasAutosuggestUrlValue) return callback()

    const url = new URL(this.autosuggestUrlValue, window.location.origin)
    if (query && query.length > 0) url.searchParams.set("q", query)

    fetch(url.toString(), {
      headers: {
        "Accept": "application/json",
        "X-CSRF-Token": this.csrfToken()
      }
    })
      .then((response) => response.json())
      .then((data) => callback(this.normalizeResults(data)))
      .catch(() => callback())
  }

  normalizeResults(data) {
    if (Array.isArray(data)) return data
    if (Array.isArray(data.services)) {
      return data.services.map((item) => ({
        value: item.slug || item.id,
        label: item.name || item.label || item.slug,
        category: item.domain || item.category
      }))
    }
    if (Array.isArray(data.action_types)) {
      return data.action_types.map((item) => ({
        value: item.slug || item.id,
        label: item.name || item.label || item.slug
      }))
    }
    return []
  }

  createRecord(input, callback) {
    if (!this.hasCreateRecordUrlValue) return callback()

    fetch(this.createRecordUrlValue, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json",
        "X-CSRF-Token": this.csrfToken()
      },
      body: JSON.stringify({ name: input })
    })
      .then((response) => response.json())
      .then((data) => callback({ value: data.value || data.slug || input, label: data.label || data.name || input, pending: data.pending }))
      .catch(() => callback())
  }

  csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content
  }

  submitOnChange() {
    if (!this.submitOnChangeValue) return

    const form = this.element.closest("form")
    form?.requestSubmit()
  }
}
